// src/helpers/locationUtils.ts

import { getGlobalVal } from "../utils";
import type { StoreCountry } from "../../locales";

export const EARTH_RADIUS_MILES = 3963.19;
const SEARCH_RADIUS = 35; // in miles

/**
 * Convert degrees to radians.
 */
function toRadians(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Computes the Haversine distance (in miles) between two geographic coordinates.
 */
export function haversineDistance(
  coord1: { lat: number; lng: number },
  coord2: { lat: number; lng: number }
): number {
  const lat1 = toRadians(coord1.lat);
  const lat2 = toRadians(coord2.lat);
  const deltaLat = toRadians(coord2.lat - coord1.lat);
  const deltaLon = toRadians(coord2.lng - coord1.lng);

  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
}

/**
 * Haversine formula to calculate distance between two lat/lng points in miles
 */
export const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number) => {
  const R = 3958.8; // Earth's radius in miles
  const rlat1 = lat1 * (Math.PI / 180); // Convert degrees to radians
  const rlat2 = lat2 * (Math.PI / 180); // Convert degrees to radians
  const difflats = rlat2 - rlat1; // Diff of latitudes
  const difflons = (lng2 - lng1) * (Math.PI / 180); // Diff of longitudes

  const d = 2 * R *
    Math.asin(
      Math.sqrt(
        Math.sin(difflats / 2) * Math.sin(difflats / 2) +
        Math.cos(rlat1) *
        Math.cos(rlat2) *
        Math.sin(difflons / 2) * Math.sin(difflons / 2)
      )
    );
  return d; // Distance in miles
};

/**
 * Uses Mapbox Geocoding API to fetch coordinates (lat, lng) for a given ZIP code.
 * Replace <YOUR_MAPBOX_TOKEN> with your actual Mapbox API token.
 */
export async function getCoordinatesFromZip(
  zipcode: string,
  country: StoreCountry = "US"
): Promise<{ lat: number; lng: number } | null> {
  const mapboxToken = `${getGlobalVal('GOOGLE_MAPBOX_API_KEY')}`;
  if (!mapboxToken) {
    console.error("Mapbox token is missing. Please set REACT_APP_MAPBOX_TOKEN in your .env file.");
    return null;
  }

  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${zipcode}.json?country=${country}&access_token=${mapboxToken}`;
  try {
    const response = await fetch(url);
    const data = await response.json();
    if (data.features && data.features.length > 0) {
      const [lng, lat] = data.features[0].center;
      return { lat, lng };
    }
  } catch (error) {
    console.error("Error fetching Mapbox data:", error);
  }
  return null;
}

/**
 * Given an array of stores (each with latitude and longitude) and user coordinates,
 * this function computes the distance for each store, sorts them by ascending distance,
 * and returns only those stores within the SEARCH_RADIUS.
 */
export function filterAndSortByDistance<T extends { latitude: number; longitude: number }>(
  stores: T[],
  userCoords: { lat: number; lng: number }
): (T & { distance: number })[] {
  const withDistance = stores.map((store) => {
    const distance = haversineDistance(
      { lat: userCoords.lat, lng: userCoords.lng },
      { lat: store.latitude, lng: store.longitude }
    );
    return { ...store, distance };
  });
  return withDistance
    .sort((a, b) => a.distance - b.distance)
    .filter((store) => store.distance <= SEARCH_RADIUS);
}
